import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import classNames from "classnames";
import { Entry, UnresolvedLink } from "contentful";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { AbstractIntlMessages, useTranslations } from "next-intl";
import { CSSProperties, useMemo } from "react";

import IconHeartPath from "@/icons/icon-heart-path";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { isActiveEntry } from "~/utils/editorial-utils";

import countries from "../../../public/json/countries.json";
import CountryDialog from "../common/country-dialog";
import Icon from "../common/icon";
import SeoHead from "../common/seo-head";
import Header from "./header";

const MiniFooter = dynamic(() => import("./mini-footer"));

type Props = {
  children: JSX.Element;
  navigation:
    | UnresolvedLink<"Entry">
    | Entry<Editorials.Nav, undefined, string>;
  footer:
    | UnresolvedLink<"Entry">
    | Entry<Editorials.MiniFooter, undefined, string>;
  page?: {
    seoMetadata?:
      | UnresolvedLink<"Entry">
      | Entry<Editorials.SeoMetadata, undefined, string>;
    contentBelowHeader?: boolean;
    navColor?: Editorials.navColor["values"];
    fallbackUrl?: string;
  } | null;
  navColor?: Editorials.navColor["values"];
  inPpd?: boolean;
  mainStyle?: CSSProperties;
  mainClasses?: string;
  slugs?: Record<string, string> | null;
  messages?: AbstractIntlMessages;
  isHomepage?: boolean;
  disableSeo?: boolean;
};

export default function LayoutPage(props: Props) {
  const router = useRouter();
  const t = useTranslations();

  // `router.asPath` includes the query string
  // This results in the `hreflang` and `canonical` meta tags to have incorrect values.
  const seoPath = useMemo(() => {
    const path = router.asPath.split("?", 1)[0];

    // NOTE: `router.asPath` includes the current locale on the server but not on the client.
    // Since this memo is isomorphic, we remove it from the start in the server environment.
    // See: https://nextjs.org/docs/pages/api-reference/functions/use-router#router-object
    return path.startsWith(`/${router.locale}/`)
      ? path.replace(`/${router.locale}/`, `/`)
      : path;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, router.locale]);

  const canonicalUrl = useMemo(() => {
    const baseUrl = `https://www.kikocosmetics.com/${router.locale}`;

    return `${baseUrl}${seoPath}`;
  }, [router.locale, seoPath]);

  const liveEntryHeader = useContentfulLiveUpdates(props.navigation);
  const liveEntryFooter = useContentfulLiveUpdates(props.footer);

  const allUnifiedCommerceLocales = useMemo(
    () =>
      countries.continents
        .flatMap((c) => c.countries)
        .filter((c) => c.isUnifiedCommerce)
        .flatMap((c) => c.cultures.map((c) => c.locale)),
    [],
  );

  const hrefLangs = useMemo(() => {
    const baseUrl = `https://www.kikocosmetics.com`;

    if (!props.slugs || Object.keys(props.slugs).length === 0) {
      return allUnifiedCommerceLocales.map((locale) => ({
        locale,
        url: `${baseUrl}/${locale}${seoPath}`,
      }));
    }

    const output: { locale: string; url: string }[] = [];

    for (let [contentfulLocale, slug] of Object.entries(props.slugs)) {
      const routerLocales = Object.entries(contentfulUtils.localesMap)
        .filter(([_, onContentful]) => onContentful === contentfulLocale)
        .filter(([onRouter]) => allUnifiedCommerceLocales.includes(onRouter))
        .map(([onRouter, _]) => onRouter);

      //SEO issues: if the slug returned by the editorial pages is "homepage", remove the slug
      slug = slug.replace(process.env.NEXT_PUBLIC_HOMEPAGE_SLUG! || "", "");

      for (const locale of routerLocales) {
        const url = new URL(`${locale}/${slug}/`.replace("//", "/"), baseUrl);
        output.push({ locale, url: url.href });
      }
    }
    return output;
  }, [allUnifiedCommerceLocales, props.slugs, seoPath]);

  return (
    <>
      {isActiveEntry(props.page?.seoMetadata) && !props.disableSeo && (
        <SeoHead
          entry={props.page.seoMetadata}
          titleSuffix={t("generic.pageTitleSuffix")}
          canonical={canonicalUrl}
          hreflangs={hrefLangs}
        />
      )}
      <div style={{ position: "absolute", opacity: 0 }} aria-hidden="true">
        <Icon icon={IconHeartPath} />
      </div>
      <Header
        entry={liveEntryHeader}
        navColor={
          props.page?.navColor
            ? props.page.navColor
            : props.navColor || "transparent"
        }
        inPpd={props.inPpd}
        messages={props.messages}
        isHomepage={props.isHomepage}
      />
      <main
        className={classNames(
          { contentBelowHeader: props.page?.contentBelowHeader || false },
          props.mainClasses,
        )}
        style={props.mainStyle}
      >
        {props.children}
      </main>
      <CountryDialog />
      <MiniFooter entry={liveEntryFooter} />
    </>
  );
}
